// NOTE: Automation API URL path constants

import type {
  IBuildRunDetailUrlProps,
  IBuildRunsUrlProps,
  IGetCronJobRunsProps,
  IGetHookRunsProps,
  IGetRunDetailProps,
  IGetWebhookRunsProps,
} from "../api/automation.types";
import { EAutomationType } from "../types/automation";
import type {
  IAutomationShareTarget,
  IRemoveAutomationShareProps,
} from "../types/automation-share";

const AUTOMATION_BASE = "/automation" as const;

// NOTE: Shared helper to build paginated runs URLs
const buildRunsUrl = ({
  baseUrl,
  uuid,
  page,
  limit,
}: IBuildRunsUrlProps): string => {
  const params = new URLSearchParams();
  if (page) {
    params.set("page", String(page));
  }
  if (limit) {
    params.set("limit", String(limit));
  }
  const query = params.toString();
  const suffix = query ? `?${query}` : "";
  return `${baseUrl}/${uuid}/runs${suffix}`;
};

// NOTE: NSAI-5266 — shared helper to build the per-run detail (trace) URL.
const buildRunDetailUrl = ({
  baseUrl,
  uuid,
  runUuid,
}: IBuildRunDetailUrlProps): string => `${baseUrl}/${uuid}/runs/${runUuid}`;

// NOTE: Cron job endpoints
export const CRON_JOBS_URL = `${AUTOMATION_BASE}/cron-jobs` as const;
// NOTE: Persists nothing, so it is not scoped under cron-jobs.
export const SCHEDULE_INTERPRET_URL =
  `${AUTOMATION_BASE}/schedule/interpret` as const;
export const cronJobUrl = (uuid: string): string => `${CRON_JOBS_URL}/${uuid}`;
export const CRON_JOBS_BULK_TOGGLE_URL =
  `${CRON_JOBS_URL}/bulk-toggle` as const;
export const CRON_JOBS_BULK_DELETE_URL =
  `${CRON_JOBS_URL}/bulk-delete` as const;
export const cronJobToggleUrl = (uuid: string): string =>
  `${CRON_JOBS_URL}/${uuid}/toggle`;
export const cronJobTriggerUrl = (uuid: string): string =>
  `${CRON_JOBS_URL}/${uuid}/trigger`;
export const cronJobRunsUrl = ({
  uuid,
  page,
  limit,
}: IGetCronJobRunsProps): string =>
  buildRunsUrl({ baseUrl: CRON_JOBS_URL, uuid, page, limit });
export const cronJobRunDetailUrl = ({
  uuid,
  runUuid,
}: IGetRunDetailProps): string =>
  buildRunDetailUrl({ baseUrl: CRON_JOBS_URL, uuid, runUuid });

// NOTE: Webhook endpoints
export const WEBHOOKS_URL = `${AUTOMATION_BASE}/webhooks` as const;
export const WEBHOOKS_BULK_TOGGLE_URL = `${WEBHOOKS_URL}/bulk-toggle` as const;
export const WEBHOOKS_BULK_DELETE_URL = `${WEBHOOKS_URL}/bulk-delete` as const;
export const webhookUrl = (uuid: string): string => `${WEBHOOKS_URL}/${uuid}`;
export const webhookToggleUrl = (uuid: string): string =>
  `${WEBHOOKS_URL}/${uuid}/toggle`;
export const webhookRegenerateTokenUrl = (uuid: string): string =>
  `${WEBHOOKS_URL}/${uuid}/regenerate-token`;
export const webhookRunsUrl = ({
  uuid,
  page,
  limit,
}: IGetWebhookRunsProps): string =>
  buildRunsUrl({ baseUrl: WEBHOOKS_URL, uuid, page, limit });
export const webhookRunDetailUrl = ({
  uuid,
  runUuid,
}: IGetRunDetailProps): string =>
  buildRunDetailUrl({ baseUrl: WEBHOOKS_URL, uuid, runUuid });

// NOTE: Hook (event rule) endpoints
export const HOOKS_URL = `${AUTOMATION_BASE}/hooks` as const;
export const HOOKS_BULK_TOGGLE_URL = `${HOOKS_URL}/bulk-toggle` as const;
export const HOOKS_BULK_DELETE_URL = `${HOOKS_URL}/bulk-delete` as const;
export const hookUrl = (uuid: string): string => `${HOOKS_URL}/${uuid}`;
export const hookToggleUrl = (uuid: string): string =>
  `${HOOKS_URL}/${uuid}/toggle`;
export const hookRunsUrl = ({ uuid, page, limit }: IGetHookRunsProps): string =>
  buildRunsUrl({ baseUrl: HOOKS_URL, uuid, page, limit });

// NOTE: NSAI-5272 — automation sharing. The kind is a path segment so one set
// of routes serves scheduled tasks and webhooks:
// `/automation/cron-jobs/:uuid/shares`, `/automation/webhooks/:uuid/shares`.
export const automationTypeSegment = (
  automationType: EAutomationType,
): string =>
  automationType === EAutomationType.CRON_JOB ? "cron-jobs" : "webhooks";

export const automationSharesUrl = ({
  automationType,
  automationUuid,
}: IAutomationShareTarget): string =>
  `${AUTOMATION_BASE}/${automationTypeSegment(automationType)}/${automationUuid}/shares`;

export const automationShareUrl = ({
  automationType,
  automationUuid,
  shareUuid,
}: IRemoveAutomationShareProps): string =>
  `${automationSharesUrl({ automationType, automationUuid })}/${shareUuid}`;

// NOTE: NSAI-5237 / NSAI-5272 — automation marketplace. Not nested under
// /automation: the publish routes take the automation kind as their first
// segment, which would collide with the automation list routes.
export const AUTOMATION_MARKETPLACE_URL = "/automation-marketplace" as const;

export const automationListingUrl = ({
  automationType,
  automationUuid,
}: IAutomationShareTarget): string =>
  `${AUTOMATION_MARKETPLACE_URL}/${automationTypeSegment(automationType)}/${automationUuid}`;

export const automationListingInstallUrl = (listingUuid: string): string =>
  `${AUTOMATION_MARKETPLACE_URL}/listings/${listingUuid}/install`;
export const hookRunDetailUrl = ({
  uuid,
  runUuid,
}: IGetRunDetailProps): string =>
  buildRunDetailUrl({ baseUrl: HOOKS_URL, uuid, runUuid });
